import React from 'react';
import { Box, Button } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import type { TAutocompleteOption } from '~/components/AutocompleteField/types.ts';
import FormAutocomplete from '~/components/FormComponents/FormAutocomplete.tsx';
import { FormCheckbox } from '~/components/FormComponents/FormCheckbox.tsx';
import { FormDateInput } from '~/components/FormComponents/FormDateInput.tsx';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import { dataFormStyles } from '~/modules/covenants/CovenantsDataForm/styles.ts';
import { guaranteesClientText } from '~/modules/guarantees/form/client/constants.ts';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import ButtonLoadingIcon from '~/modules/guarantees/form/shared/ButtonLoadingIcon.tsx';
import { GUARANTEE_OPERATION_TYPE_OPTIONS } from '../../../rootSection/constants.ts';
import type { TGuaranteesRootSectionFormValues } from '../../../rootSection/types.ts';
import FlowSection from '../shared/FlowSection.tsx';
import { useGuaranteesRootApplicationSectionContext } from '../RootApplicationSectionContext.tsx';

interface IGuaranteeTypeSectionProps {
  isGuaranteeTypeSelected: boolean;
  isOtherOperationType: boolean;
  isProzorroTender: boolean;
  guaranteeCurrencyOptions: TAutocompleteOption[];
  isLoadingCurrencies: boolean;
  isSaving: boolean;
  hasChanges?: boolean;
}

const GuaranteeTypeSection: React.FC<IGuaranteeTypeSectionProps> = ({
  isGuaranteeTypeSelected,
  isOtherOperationType,
  isProzorroTender,
  guaranteeCurrencyOptions,
  isLoadingCurrencies,
  isSaving,
  hasChanges = false,
}) => {
  const { isReadOnly, requireChangesToEnableActions } =
    useGuaranteesRootApplicationSectionContext();
  const { control } = useFormContext<TGuaranteesRootSectionFormValues>();
  const currentGuaranteeCurrency = useWatch({
    control,
    name: 'guaranteeCurrency',
  });
  const isSaveButtonDisabled =
    isSaving || isReadOnly || (requireChangesToEnableActions && !hasChanges);
  const displayedGuaranteeCurrencyOptions = React.useMemo(() => {
    if (
      !currentGuaranteeCurrency?.value ||
      guaranteeCurrencyOptions.some((option) => option.value === currentGuaranteeCurrency.value)
    ) {
      return guaranteeCurrencyOptions;
    }

    return [currentGuaranteeCurrency, ...guaranteeCurrencyOptions];
  }, [currentGuaranteeCurrency, guaranteeCurrencyOptions]);

  return (
    <FlowSection
      step={guaranteesClientText.rootSectionStepLabel}
      title={guaranteesClientText.rootSectionTitle}
      description={guaranteesClientText.rootSectionDescription}
    >
      <Box sx={guaranteesClientPageStyles.flowFieldsGrid}>
        <FormAutocomplete
          name="guaranteeOperationType"
          label={guaranteesClientText.guaranteeOperationTypeLabel}
          placeholder={guaranteesClientText.guaranteeOperationTypePlaceholder}
          options={GUARANTEE_OPERATION_TYPE_OPTIONS}
          disabled={isReadOnly}
          sx={guaranteesClientPageStyles.flowFullWidthField}
        />

        {isOtherOperationType && (
          <FormInput
            name="guaranteeOperationTypeOther"
            label={guaranteesClientText.guaranteeOperationTypeOtherLabel}
            placeholder={guaranteesClientText.guaranteeOperationTypeOtherPlaceholder}
            disabled={isReadOnly}
            sx={guaranteesClientPageStyles.flowFullWidthField}
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
          />
        )}

        {isGuaranteeTypeSelected && !isProzorroTender && (
          <>
            <FormInput
              name="guaranteeAmount"
              label={guaranteesClientText.guaranteeAmountLabel}
              type="number"
              disabled={isReadOnly}
              sx={guaranteesClientPageStyles.flowField}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
                htmlInput: {
                  min: 0,
                  step: '0.01',
                },
              }}
            />

            <FormAutocomplete
              name="guaranteeCurrency"
              label={guaranteesClientText.guaranteeCurrencyLabel}
              placeholder={guaranteesClientText.guaranteeCurrencyPlaceholder}
              options={displayedGuaranteeCurrencyOptions}
              disabled={isReadOnly || isLoadingCurrencies}
              sx={guaranteesClientPageStyles.flowField}
            />

            <FormDateInput
              name="guaranteeExpiryDate"
              label={guaranteesClientText.guaranteeExpiryDateLabel}
              disabled={isReadOnly}
              sx={guaranteesClientPageStyles.flowField}
            />
          </>
        )}

        <Box sx={guaranteesClientPageStyles.flowFullWidthField}>
          <FormCheckbox
            name="isUrgent"
            label={guaranteesClientText.urgentReviewLabel}
            disabled={isReadOnly}
          />
        </Box>
      </Box>

      <Box sx={guaranteesClientPageStyles.flowActions}>
        <Button
          type="submit"
          variant="contained"
          disabled={isSaveButtonDisabled}
          startIcon={isSaving ? <ButtonLoadingIcon /> : undefined}
          sx={dataFormStyles.documentPrimaryAction}
        >
          {guaranteesClientText.saveButton}
        </Button>
      </Box>
    </FlowSection>
  );
};

export default GuaranteeTypeSection;
